import { DayPilot } from '@daypilot/daypilot-lite-react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import BrowserRouter from '@fuse/core/BrowserRouter';
import FuseAuthorization from '@fuse/core/FuseAuthorization';
import FuseLayout from '@fuse/core/FuseLayout';
import FuseTheme from '@fuse/core/FuseTheme';
import settingsConfig from 'app/configs/settingsConfig';
import { selectMainThemeDark, selectMainThemeLight } from 'app/store/fuse/settingsSlice';
import themeLayouts from 'app/theme-layouts/themeLayouts';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import isBetween from 'dayjs/plugin/isBetween';
import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AuthProvider } from './auth/AuthContext';
import fallbackRender from './ErrorBoundaryFallbackRender';
import { useUser } from './hooks/useUser';
import { useCheckAppointmentManagerQuery } from './services/resourceApi';
import { useCheckSubordinateQuery } from './services/shtatApi';
import { removeNavigationItem } from './store/fuse/navigationSlice';
import { addUserRole, setHasSubordinates } from './store/userSlice';
import withAppProviders from './withAppProviders';

const localeUZ = new DayPilot.Locale('uz-uz', {
  dayNames: ['Yakshanba', 'Dushanba', 'Seshanba', 'Chorshanba', 'Payshanba', 'Juma', 'Shanba'],
  dayNamesShort: ['Yak', 'Du', 'Se', 'Cho', 'Pay', 'Ju', 'Sha'],
  monthNames: [
    'Yanvar',
    'Fevral',
    'Mart',
    'Aprel',
    'May',
    'Iyun',
    'Iyul',
    'Avgust',
    'Sentabr',
    'Oktabr',
    'Noyabr',
    'Dekabr',
  ],
  timePattern: 'HH:mm',
  datePattern: 'DD.MM.YYYY',
  dateTimePattern: 'DD.MM.YYYY HH:mm',
  timeFormat: 'Clock24Hours',
  weekStarts: 1,
  weekend: [6, 0],
});

const App = () => {
  dayjs.locale('ru');
  dayjs.extend(isBetween);
  DayPilot.Locale.register(localeUZ);

  const { isTop, user, isManager } = useUser();
  const isDarkMode = useSelector((state) => state.themeSlice.isDarkMode);
  const selectedTheme = useSelector(isDarkMode ? selectMainThemeDark : selectMainThemeLight);
  const { data: hasSubordinates = false, isSuccess: gotSubordinatesStatus } =
    useCheckSubordinateQuery({}, { skip: !user?.id });
  const { data: isAppointmentManager, isSuccess: gotAppointmentStatus } =
    useCheckAppointmentManagerQuery({}, { skip: !user?.id });
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isTop || user?.staffId === 0) {
      dispatch(removeNavigationItem('workload'));
      dispatch(removeNavigationItem('skud-departments'));
      dispatch(removeNavigationItem('daily-attendances'));
    }
  }, [isTop]);

  useEffect(() => {
    if (hasSubordinates && !isManager) {
      dispatch(setHasSubordinates(true));
      dispatch(addUserRole('manager'));
    }
  }, [hasSubordinates, isManager]);

  useEffect(() => {
    if (user?.staffId > 0) {
      dispatch(addUserRole('employee'));
    }
  }, [user?.staffId]);

  useEffect(() => {
    if ((!isAppointmentManager && gotAppointmentStatus) || user?.staffId === 0) {
      dispatch(removeNavigationItem('apps.appointments'));
      dispatch(removeNavigationItem('skud-departments-report'));
    }
  }, [isAppointmentManager, gotAppointmentStatus]);

  const snowflake1 = document.createElement('img');
  snowflake1.src = isDarkMode ? 'assets/images/snowflake_white.png' : 'assets/images/snowflake.png';
  snowflake1.style.width = '400px';

  return (
    <ErrorBoundary FallbackComponent={fallbackRender}>
      <CacheProvider
        value={createCache({
          key: 'muiltr',
          stylisPlugins: [],
          insertionPoint: document.getElementById('emotion-insertion-point'),
        })}
      >
        <FuseTheme theme={selectedTheme} direction={'ltr'}>
          {/* <div className="z-9999">
            <Snowfall
              snowflakeCount={40}
              images={[snowflake1]}
              radius={[10.0, 20.0]}
              speed={[0.2, 1.0]}
              wind={[-0.5, 1.0]}
            />
          </div> */}
          <AuthProvider>
            <BrowserRouter>
              <FuseAuthorization
                userRole={user.roles}
                loginRedirectUrl={settingsConfig.loginRedirectUrl}
              >
                <FuseLayout layouts={themeLayouts} />
              </FuseAuthorization>
            </BrowserRouter>
          </AuthProvider>
        </FuseTheme>
      </CacheProvider>
    </ErrorBoundary>
  );
};

export default withAppProviders(App)();
